import { useQuery } from '@tanstack/react-query';
import { Apple, Send as SendIcon, Smartphone, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { LoadingButton } from '@/components/shared/loading-button';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import { platformsReady } from '@/lib/credentials';
import { fmtNum } from '@/lib/format';
import type { AppDetail, Environment } from '@/lib/types';

type Estimate = { total: number; android: number; ios: number; max_recipients: number };

/** "Review Your Message": who it reaches and what it says, confirmed before anything is sent. */
export function ReviewDialog({ open, onOpenChange, app, env, title, body, actionUrl, data, sending, onSend }: {
  open: boolean; onOpenChange(open: boolean): void; app: AppDetail; env: Environment;
  title: string; body: string; actionUrl: string; data: string; sending: boolean; onSend(): void;
}) {
  // Fresh each time it opens: the audience may have changed since the last review.
  const estimate = useQuery({
    queryKey: ['broadcast-estimate', app.id, env],
    queryFn: () => api<Estimate>(`/admin/v1/apps/${app.id}/broadcast/estimate`, { query: { environment: env } }),
    enabled: open, staleTime: 0,
  });
  const e = estimate.data;
  const ready = platformsReady(app, env);
  const missing = e ? (['android', 'ios'] as const).filter((p) => e[p] > 0 && !ready[p]) : [];
  const reachable = e ? (ready.android ? e.android : 0) + (ready.ios ? e.ios : 0) : 0;
  const blocked = !e || e.total === 0 || e.total > e.max_recipients || reachable === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!sending) onOpenChange(o); }}>
      <DialogContent className="gap-0 p-0 sm:max-w-xl">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>Review Your Message</DialogTitle>
          <DialogDescription className="sr-only">Check the audience and content before sending.</DialogDescription>
        </DialogHeader>

        <div className="max-h-[65vh] overflow-y-auto px-6">
          <Section title="Audience">
            <Row k="App">{app.name} <span className="text-muted-foreground capitalize">({env})</span></Row>
            <Row k="Estimated recipients">
              {estimate.isLoading ? <Skeleton className="h-4 w-24" /> : e ? (
                <span className="inline-flex flex-wrap items-center gap-x-3">
                  <b className="font-medium tabular-nums">{fmtNum(e.total)}</b>
                  <span className="text-xs text-muted-foreground">Android {fmtNum(e.android)} · iOS {fmtNum(e.ios)}</span>
                </span>
              ) : <span className="text-muted-foreground">—</span>}
            </Row>
          </Section>
          <Separator />
          <Section title="Message">
            <Row k="Platforms">
              {e && (e.android > 0 || e.ios > 0) ? (
                <span className="inline-flex items-center gap-2">
                  {e.android > 0 && <PlatformMark label="Android"><Smartphone className="size-4" /></PlatformMark>}
                  {e.ios > 0 && <PlatformMark label="iOS"><Apple className="size-4" /></PlatformMark>}
                </span>
              ) : <span className="text-muted-foreground">—</span>}
            </Row>
            <Row k="Title"><span className="break-words">{title}</span></Row>
            <Row k="Description">{body ? <span className="break-words whitespace-pre-line">{body}</span> : <span className="text-muted-foreground">—</span>}</Row>
            {actionUrl && <Row k="Action URL"><span className="font-mono text-xs break-all">{actionUrl}</span></Row>}
            {data.trim() && <Row k="Data"><span className="font-mono text-xs break-all">{data.trim()}</span></Row>}
          </Section>

          {e && e.total === 0 && <Warning>There are no active devices in {env}. Nothing would be sent.</Warning>}
          {e && e.total > 0 && missing.length > 0 && (
            <Warning>
              No {missing.map((p) => (p === 'ios' ? 'iOS' : 'Android')).join(' or ')} credentials in {env}
              {reachable === 0
                ? ', so no device can be reached.'
                : `: ${fmtNum(missing.reduce((n, p) => n + e[p], 0))} ${missing.map((p) => (p === 'ios' ? 'iOS' : 'Android')).join(' and ')} devices will fail.`}
            </Warning>
          )}
          {e && e.total > e.max_recipients && (
            <Warning>This environment has {fmtNum(e.total)} active devices; sending to everyone is limited to {fmtNum(e.max_recipients)} for now.</Warning>
          )}
        </div>

        <DialogFooter className="border-t px-6 py-3">
          <Button variant="outline" disabled={sending} onClick={() => onOpenChange(false)}>Edit</Button>
          <LoadingButton disabled={blocked} loading={sending} onClick={onSend}><SendIcon /> Send Message</LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="py-4">
    <h3 className="mb-2.5 text-sm font-semibold">{title}</h3>
    <dl className="grid gap-y-2.5 text-sm">{children}</dl>
  </section>
);

const Row = ({ k, children }: { k: string; children: ReactNode }) => (
  <div className="grid grid-cols-[10rem_1fr] items-start gap-4">
    <dt className="text-muted-foreground">{k}</dt>
    <dd className="min-w-0">{children}</dd>
  </div>
);

const PlatformMark = ({ label, children }: { label: string; children: ReactNode }) => (
  <Tooltip>
    <TooltipTrigger asChild><span className="grid size-7 place-items-center rounded-md border bg-muted" aria-label={label}>{children}</span></TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

const Warning = ({ children }: { children: ReactNode }) => (
  <p className="mb-4 flex gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
    <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-warning" /> <span className="min-w-0">{children}</span>
  </p>
);
